import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Database, Trash2, RefreshCw, Tag, Users, Briefcase, Calendar,
  CreditCard, MessageSquare, AlertTriangle, CheckCircle, Loader2,
  UserCog, Wrench, MapPin, Bell, Flag
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function DataManagement() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/sample-data/stats`, { headers });
      setStats(res.data);
    } catch (error) {
      toast.error('Failed to load data statistics');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleMarkExisting = async () => {
    setIsProcessing(true);
    try {
      const res = await axios.post(`${API}/admin/sample-data/mark-existing`, {}, { headers });
      toast.success('Existing data marked as sample');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark data');
    } finally {
      setIsProcessing(false);
      setShowConfirmDialog(false);
    }
  };

  const handleClearSample = async () => {
    if (!password) {
      toast.error('Password is required for this operation');
      return;
    }
    setIsProcessing(true);
    try {
      const res = await axios.delete(`${API}/admin/sample-data/clear`, { 
        headers,
        data: { password }
      });
      toast.success('Sample data cleared successfully');
      setPassword('');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear data');
    } finally {
      setIsProcessing(false);
      setShowConfirmDialog(false);
    }
  };

  const handleResetAndReseed = async () => {
    if (!password) {
      toast.error('Password is required for this operation');
      return;
    }
    setIsProcessing(true);
    try {
      const res = await axios.post(`${API}/admin/sample-data/reset-and-reseed`, { password }, { headers });
      toast.success('Sample data reset and re-seeded');
      setPassword('');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset data');
    } finally {
      setIsProcessing(false);
      setShowConfirmDialog(false);
    }
  };

  const openConfirmDialog = (action) => {
    setConfirmAction(action);
    setPassword('');
    setShowPassword(false);
    setShowConfirmDialog(true);
  };

  const executeAction = () => {
    switch (confirmAction) {
      case 'mark':
        handleMarkExisting();
        break;
      case 'clear':
        handleClearSample();
        break;
      case 'reset':
        handleResetAndReseed();
        break;
      default:
        break;
    }
  };

  const getConfirmContent = () => {
    switch (confirmAction) {
      case 'mark':
        return {
          title: 'Mark Existing Data as Sample',
          description: 'This will mark all existing customers, providers, bookings, transactions, and related data as "sample data". Admin and Manager accounts will NOT be affected. This allows you to clear this data later.',
          buttonText: 'Mark as Sample',
          buttonClass: 'bg-blue-600 hover:bg-blue-700'
        };
      case 'clear':
        return {
          title: 'Clear All Sample Data',
          description: 'This will PERMANENTLY DELETE all data marked as sample, including: users, wallets, bookings, transactions, reviews, disputes, and more. Admin and Manager accounts will NOT be affected. This action cannot be undone!',
          buttonText: 'Delete Sample Data',
          buttonClass: 'bg-red-600 hover:bg-red-700'
        };
      case 'reset':
        return {
          title: 'Reset and Re-seed Sample Data',
          description: 'This will clear all sample data and then create fresh sample users, bookings, and transactions. Useful for demo purposes or testing. Admin and Manager accounts will NOT be affected.',
          buttonText: 'Reset & Re-seed',
          buttonClass: 'bg-amber-600 hover:bg-amber-700'
        };
      default:
        return {};
    }
  };

  const StatCard = ({ icon: Icon, label, sample, real, admin, color }) => (
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <span className="font-medium text-slate-900">{label}</span>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-500">Sample Data</span>
          <span className="text-lg font-semibold text-amber-600">{sample || 0}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-500">Real Data</span>
          <span className="text-lg font-semibold text-green-600">{real || 0}</span>
        </div>
        {admin !== undefined && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-500">Admin/Manager</span>
            <span className="text-lg font-semibold text-blue-600">{admin}</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-100">
      <AdminSidebar />
      <AdminMobileNav />
      
      <main className="md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">Data Management</h1>
          <p className="text-slate-500 mt-1">Manage sample and test data in the system</p>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">About Sample Data</p>
              <p className="text-sm text-blue-700 mt-1">
                Sample data includes test users, providers, staff members, services, bookings, transactions, reviews, disputes, 
                user reports, wallets, addresses, chat messages, and notifications.
                Admin and Manager accounts are <strong>NEVER</strong> affected by sample data operations.
                Smart identification automatically detects test data by email patterns (@test.com, @example.com, @provider.com, @customer.com) 
                and name patterns (TEST_, Sample, Demo).
              </p>
            </div>
          </div>
        </div>

        {/* Statistics */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : stats ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard 
              icon={Users} 
              label="Users" 
              sample={stats.users?.sample} 
              real={stats.users?.real} 
              admin={stats.users?.admin}
              color="bg-slate-700" 
            />
            <StatCard 
              icon={Briefcase} 
              label="Provider Profiles" 
              sample={stats.provider_profiles?.sample} 
              real={stats.provider_profiles?.real} 
              color="bg-blue-600" 
            />
            <StatCard 
              icon={UserCog} 
              label="Provider Staff" 
              sample={stats.provider_staff?.sample} 
              real={stats.provider_staff?.real} 
              color="bg-indigo-600" 
            />
            <StatCard 
              icon={Wrench} 
              label="Provider Services" 
              sample={stats.provider_services?.sample} 
              real={stats.provider_services?.real} 
              color="bg-cyan-600" 
            />
            <StatCard 
              icon={Calendar} 
              label="Bookings" 
              sample={stats.bookings?.sample} 
              real={stats.bookings?.real} 
              color="bg-green-600" 
            />
            <StatCard 
              icon={CreditCard} 
              label="Transactions" 
              sample={stats.transactions?.sample} 
              real={stats.transactions?.real} 
              color="bg-purple-600" 
            />
            <StatCard 
              icon={CreditCard} 
              label="Wallets" 
              sample={stats.wallets?.sample} 
              real={stats.wallets?.real} 
              color="bg-teal-600" 
            />
            <StatCard 
              icon={MapPin} 
              label="Addresses" 
              sample={stats.addresses?.sample} 
              real={stats.addresses?.real} 
              color="bg-orange-600" 
            />
            <StatCard 
              icon={MessageSquare} 
              label="Reviews" 
              sample={stats.reviews?.sample} 
              real={stats.reviews?.real} 
              color="bg-amber-600" 
            />
            <StatCard 
              icon={AlertTriangle} 
              label="Disputes" 
              sample={stats.disputes?.sample} 
              real={stats.disputes?.real} 
              color="bg-red-600" 
            />
            <StatCard 
              icon={Flag} 
              label="User Reports" 
              sample={stats.reports?.sample} 
              real={stats.reports?.real} 
              color="bg-rose-600" 
            />
            <StatCard 
              icon={MessageSquare} 
              label="Chat Messages" 
              sample={stats.chat_messages?.sample} 
              real={stats.chat_messages?.real} 
              color="bg-pink-600" 
            />
            <StatCard 
              icon={Bell} 
              label="Notifications" 
              sample={stats.notifications?.sample} 
              real={stats.notifications?.real} 
              color="bg-violet-600" 
            />
          </div>
        ) : null}

        {/* Actions */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Actions</h2>
          
          <div className="space-y-4">
            {/* Mark Existing */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-stone-50 rounded-xl gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Tag className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Mark Existing Data as Sample</p>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Tag all current non-admin users and their data as sample. New registrations will be "real".
                  </p>
                </div>
              </div>
              <Button
                onClick={() => openConfirmDialog('mark')}
                className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                data-testid="mark-sample-btn"
              >
                <Tag className="w-4 h-4 mr-2" />
                Mark as Sample
              </Button>
            </div>

            {/* Clear Sample */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-red-50 rounded-xl gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Clear Sample Data</p>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Permanently delete all data marked as sample. Admin/Manager accounts are protected.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => openConfirmDialog('clear')}
                variant="destructive"
                className="whitespace-nowrap"
                data-testid="clear-sample-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Sample Data
              </Button>
            </div>

            {/* Reset and Reseed */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-amber-50 rounded-xl gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <RefreshCw className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Reset & Re-seed Sample Data</p>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Clear all sample data and create fresh test users, bookings, and transactions.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => openConfirmDialog('reset')}
                className="bg-amber-600 hover:bg-amber-700 whitespace-nowrap"
                data-testid="reset-sample-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset & Re-seed
              </Button>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-medium text-slate-900 mb-3">Data Types</h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-amber-500"></span>
              <span className="text-sm text-slate-600">Sample Data (can be deleted)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              <span className="text-sm text-slate-600">Real Data (protected)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-500"></span>
              <span className="text-sm text-slate-600">Admin/Manager (always protected)</span>
            </div>
          </div>
        </div>
      </main>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{getConfirmContent().title}</DialogTitle>
            <DialogDescription className="pt-2">
              {getConfirmContent().description}
            </DialogDescription>
          </DialogHeader>
          
          {/* Password field for destructive operations */}
          {(confirmAction === 'clear' || confirmAction === 'reset') && (
            <div className="mt-4 space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Data Management Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password to continue"
                  className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="data-management-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-xs text-slate-500">This password protects destructive operations.</p>
            </div>
          )}
          
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isProcessing}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={executeAction}
              disabled={isProcessing || ((confirmAction === 'clear' || confirmAction === 'reset') && !password)}
              className={`flex-1 ${getConfirmContent().buttonClass}`}
              data-testid="confirm-action-btn"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                getConfirmContent().buttonText
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
